/**
 * Valorile tastate care NU au ajuns (inca) pe server, si starea de salvare per celula.
 *
 * Regula din plan: **la esec valoarea RĂMÂNE**. Rollbackul optimist readuce in cache datele
 * SERVERULUI — altfel totalul zilei ar contine o suma care nu s-a scris nicaieri — dar textul
 * tastat de om nu are voie sa dispara odata cu el. De aceea sta aici, deliberat IN AFARA cache-ului
 * de query: ce a scris un om nu e o data de server, deci nu are voie sa fie stearsa de un
 * `invalidateQueries`, de un garbage collect sau de un rollback.
 *
 * Se pastreaza TEXTUL BRUT, nu valoarea parsata: `2.515,67`, `2515.67` si `=2515.67+2501.07` sunt
 * trei lucruri diferite pentru omul care le-a scris, iar 69 de celule din foaia principala sunt
 * expresii. Reafisarea unei sume „normalizate" dupa un esec ar fi o a doua pierdere, dupa cea a
 * salvarii.
 *
 * Magazinul e comun ecranului zilei si comparatorului de curs: aceeasi mecanica de celula, deci
 * aceeasi implementare — doua copii ar diverge la primul bugfix.
 */

import { useMemo, useSyncExternalStore } from 'react';

/** Din ce grila vine celula. Face cheia unica fara sa amestece id-urile a doua tabele diferite. */
export type ZonaEditare = 'intrare' | 'cotatie' | 'pozitie';

export type StareEditare =
  /** Tastat, inca nesupus (`dirty`). */
  | 'modificat'
  /** Trimis si refuzat. Chip `Nesalvat` + `Reîncearcă`. */
  | 'nesalvat'
  /** Refuzat cu 409: exista o versiune mai noua pe server, valoarea se REAPLICA peste ea. */
  | 'conflict';

export interface EditareLocala {
  zona: ZonaEditare;
  id: number;
  camp: string;
  /** Textul EXACT tastat. Nu se reformateaza si nu se reparseaza niciodata. */
  valoare: string;
  stare: StareEditare;
  mesaj: string | null;
  la: number;
}

function cheie(zona: ZonaEditare, id: number, camp: string): string {
  return `${zona}:${id}:${camp}`;
}

class MagazinEditari {
  #valori = new Map<string, EditareLocala>();
  #abonati = new Set<() => void>();
  #instantaneu: readonly EditareLocala[] = [];

  aboneaza = (asculta: () => void): (() => void) => {
    this.#abonati.add(asculta);
    return () => {
      this.#abonati.delete(asculta);
    };
  };

  /**
   * Referinta STABILA intre notificari. `useSyncExternalStore` compara instantaneele cu `===`, deci
   * un `[...map.values()]` calculat la fiecare citire ar face React sa redeseneze la infinit.
   */
  instantaneu = (): readonly EditareLocala[] => this.#instantaneu;

  valoare = (zona: ZonaEditare, id: number, camp: string): EditareLocala | undefined =>
    this.#valori.get(cheie(zona, id, camp));

  marcheaza = (editare: EditareLocala): void => {
    this.#valori.set(cheie(editare.zona, editare.id, editare.camp), editare);
    this.#notifica();
  };

  curata = (zona: ZonaEditare, id: number, campuri: readonly string[]): void => {
    let schimbat = false;
    for (const camp of campuri) {
      if (this.#valori.delete(cheie(zona, id, camp))) schimbat = true;
    }
    if (schimbat) this.#notifica();
  };

  curataTot = (): void => {
    if (this.#valori.size === 0) return;
    this.#valori.clear();
    this.#notifica();
  };

  #notifica(): void {
    this.#instantaneu = [...this.#valori.values()];
    for (const asculta of this.#abonati) asculta();
  }
}

/** Singleton pe tab. Trăiește cat trăiește pagina si moare doar cand valoarea ajunge pe server. */
export const magazinEditari = new MagazinEditari();

export function useEditariLocale(): readonly EditareLocala[] {
  return useSyncExternalStore(
    magazinEditari.aboneaza,
    magazinEditari.instantaneu,
    magazinEditari.instantaneu,
  );
}

export function useEditareLocala(
  zona: ZonaEditare,
  id: number,
  camp: string,
): EditareLocala | undefined {
  const editari = useEditariLocale();
  return useMemo(
    () => editari.find((e) => e.zona === zona && e.id === id && e.camp === camp),
    [editari, zona, id, camp],
  );
}

// ---------------------------------------------------------------------------
// Starea de salvare
// ---------------------------------------------------------------------------

export type StareSalvare = 'curat' | 'modificat' | 'salvez' | 'salvat' | 'nesalvat';

/** Cat ramane vizibila bifa `salvat` inainte sa se stinga singura. */
export const DURATA_SALVAT_MS = 2_500;

export interface StareMutatieCelula {
  status: 'idle' | 'pending' | 'success' | 'error';
  submittedAt: number;
}

/**
 * `modificat → salvez → salvat`, derivat pur.
 *
 * Extras din hook ca sa poata fi testat fara React: e singura logica din tot stratul care decide ce
 * scrie in coltul celulei, si e exact locul unde o greseala se vede ca „aplicatia zice ca a salvat,
 * dar nu a salvat".
 *
 * Ordinea conteaza: o mutatie in zbor bate orice, pentru ca ea E cea mai noua informatie. Abia dupa
 * ea conteaza o valoare locala ramasa (un esec sau o editare nesupusa), si abia la urma istoricul.
 */
export function derivaStareCelula(
  stari: readonly StareMutatieCelula[],
  editare: EditareLocala | undefined,
  acum: number = Date.now(),
): StareSalvare {
  if (stari.some((s) => s.status === 'pending')) return 'salvez';
  if (editare) return editare.stare === 'modificat' ? 'modificat' : 'nesalvat';

  const ultima = [...stari].sort((a, b) => b.submittedAt - a.submittedAt)[0];
  if (!ultima) return 'curat';
  if (ultima.status === 'error') return 'nesalvat';
  if (ultima.status === 'success' && acum - ultima.submittedAt < DURATA_SALVAT_MS) return 'salvat';
  return 'curat';
}
